import type { ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Alert, Anchor, Group, Pagination, Skeleton as MSkeleton, Stack, Text } from '@mantine/core'
import { IconAlertTriangle, IconDatabaseOff, IconInfoCircle, IconRadar } from '@tabler/icons-react'
import { FilterChip, PageTitle, SectionCard } from '@lighthouse/design'
import { RulesPanel } from '../components/signals/RulesPanel'
import { SignalDrawer } from '../components/signals/SignalDrawer'
import { SignalEmptyState, SignalErrorState } from '../components/signals/SignalBits'
import { SignalTable } from '../components/signals/SignalTable'
import {
  DETECTOR_ONLY, HANDLING_FILTER_LABEL, NOT_CORRELATED, coverageText, detectorBanner, detectorHeaderText, emptyView,
  hasInboxFilters, inboxListQuery, inboxQueryFromSearch, inboxSearchFromQuery, ruleName, subjectKindLabel,
} from '../components/signals/signalFormat'
import type { DetectorBanner, InboxFilters } from '../components/signals/signalFormat'
import { handlingUnavailableText, inboxCountLine } from '../components/signals/signalHandlingFormat'
import { queryView } from '../components/overview/overviewFormat'
import { useSignalSummary, useSignals } from '../hooks/useSignals'
import { SIGNAL_DEFAULT_TAKE, SIGNAL_HANDLING_FILTERS, SIGNAL_SEVERITIES, SIGNAL_SUBJECT_KINDS } from '../types/signals'
import type { SignalList, SignalListState, SignalSummary } from '../types/signals'

/** One page of the list. The endpoint's own default, so the default view sends no `take`. */
const PAGE_SIZE = SIGNAL_DEFAULT_TAKE

/**
 * Signal Inbox (Signal Deck) — the conditions Automaton's detector raised from what Observatory already measures:
 * active ETL halts, stale runs, critical Data Comb findings, unreachable sources and dead-lettered messages.
 *
 * Four decisions shape the page:
 *
 *  1. **People handle; only the detector ends.** Signals are raised and cleared by the detector alone. An
 *     administrator may acknowledge one ("I have got this"), hand it to someone, or release it (D6b) — from the
 *     drawer, behind the handling gate — and none of that closes or hides it: there is no resolve, snooze or
 *     dismiss. The Handling column says who has each; the count line says "N open · M acknowledged", never N - M.
 *     The table itself carries no action control. The page says all of this.
 *  2. **Not correlated.** One failure can raise several signals (a halted run also goes stale). The page says so,
 *     always, rather than implying each row is a separate problem. Incidents stay out (doc 05).
 *  3. **The summary decides what the list means.** It is read first: an unprovisioned store, a detector that never
 *     ran, and one that has stopped reporting are banners of their own, and only Complete coverage turns an empty
 *     list into an all-clear. The list is not asked until the summary says there is a store and a detector —
 *     which is also how a `rule` filter is checked against the rules the server served before it is sent.
 *  4. **Everything is in the URL.** `state`, `severity`, `rule`, `kind`, `observation=unconfirmed`, `handling`,
 *     `page` and `signal` (the drawer). An invalid value is dropped, never sent (signalFormat.inboxQueryFromSearch) —
 *     and `handling` is dropped unless the summary says the handling table exists, because the endpoint 503s it.
 */
export function SignalInboxPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const summaryQuery = useSignalSummary()
  const view = queryView(summaryQuery)
  const summary = view === 'ready' ? summaryQuery.data! : null

  const filters = inboxQueryFromSearch(
    searchParams,
    summary ? summary.rules.map((r) => r.ruleId) : [],
    summary?.handlingProvisioned === true,
  )
  const listable = summary !== null && summary.storeProvisioned && !summary.detector.hasNeverRun
  const list = useSignals(inboxListQuery(filters, PAGE_SIZE), { enabled: listable })

  const go = (next: InboxFilters, replace = false) => setSearchParams(inboxSearchFromQuery(next), { replace })
  const withSignal = (id: string | null) => `?${inboxSearchFromQuery({ ...filters, signalId: id }).toString()}`

  return (
    <Stack gap="lg" pb="xl">
      <PageTitle subtitle="Signal Deck — what the detector raised from conditions Observatory already measures">
        Signal Inbox
      </PageTitle>

      <Alert variant="light" color="gray" icon={<IconInfoCircle size={16} />} data-signal-limits="">
        <Text size="sm">{NOT_CORRELATED} {DETECTOR_ONLY}</Text>
      </Alert>

      {view === 'error' ? (
        <SignalErrorState error={summaryQuery.error} />
      ) : view === 'loading' || !summary ? (
        <Stack gap="md" data-signal-page="loading" aria-busy="true">
          <MSkeleton height={48} />
          <MSkeleton height={240} />
        </Stack>
      ) : (
        <>
          <DetectorStatus summary={summary} />

          {listable && (
            <SignalListSection
              summary={summary}
              filters={filters}
              list={list}
              onFilter={(next) => go({ ...filters, ...next, page: 1 })}
              onPage={(page) => go({ ...filters, page })}
              detailHref={(id) => withSignal(id)}
            />
          )}

          <RulesPanel summary={summary} />
        </>
      )}

      <SignalDrawer
        signalId={filters.signalId}
        onClose={() => go({ ...filters, signalId: null }, true)}
        signalHref={(id) => withSignal(id)}
      />
    </Stack>
  )
}

/** The header line, or the banner that replaces it when the detector cannot be believed. */
function DetectorStatus({ summary }: { summary: SignalSummary }) {
  const banner = detectorBanner(summary)
  if (banner) return <DetectorBannerView banner={banner} />
  return (
    <Stack gap={2} data-signal-detector="reporting">
      <Group gap={6}>
        <IconRadar size={15} style={{ color: 'var(--color-text-secondary)' }} />
        <Text size="sm" fw={500}>{detectorHeaderText(summary)}</Text>
      </Group>
      <Text size="xs" c="dimmed">{coverageText(summary)}</Text>
    </Stack>
  )
}

function DetectorBannerView({ banner }: { banner: DetectorBanner }) {
  const stale = banner.kind === 'stale'
  return (
    <Alert
      data-signal-detector={banner.kind}
      variant="light"
      color={stale ? 'yellow' : 'gray'}
      icon={stale ? <IconAlertTriangle size={16} /> : banner.kind === 'unprovisioned' ? <IconDatabaseOff size={16} /> : <IconRadar size={16} />}
      title={banner.title}
    >
      <Text size="sm">{banner.message}</Text>
    </Alert>
  )
}

const STATE_CHIPS: { value: SignalListState; label: string }[] = [
  { value: 'open', label: 'Open' },
  // Not "Resolved": most endings (no longer reported, removed, out of scope) are not proof that anything recovered.
  { value: 'resolved', label: 'No longer open' },
  { value: 'all', label: 'All' },
]

function ChipGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Group gap={6} wrap="wrap" align="center">
      <Text size="xs" c="dimmed" fw={600} style={{ minWidth: 64 }}>{label}</Text>
      {children}
    </Group>
  )
}

function SignalListSection({ summary, filters, list, onFilter, onPage, detailHref }: {
  summary: SignalSummary
  filters: InboxFilters
  list: { data: SignalList | undefined; isLoading: boolean; isError: boolean; error: unknown }
  onFilter: (next: Partial<InboxFilters>) => void
  onPage: (page: number) => void
  detailHref: (id: string) => string
}) {
  const listView = queryView(list)
  const data = listView === 'ready' ? list.data! : null
  const pages = data ? Math.max(1, Math.ceil(data.totalCount / PAGE_SIZE)) : 1
  const handlingUnavailable = handlingUnavailableText(summary)

  return (
    <SectionCard
      title="Signals"
      subtitle={data ? inboxCountLine(data, filters, summary) : undefined}
      flush
    >
      <Stack gap={8} p="md" pb={4} data-signal-filters="">
        <ChipGroup label="State">
          {STATE_CHIPS.map((chip) => (
            <FilterChip key={chip.value} active={filters.state === chip.value} onClick={() => onFilter({ state: chip.value })}>
              {chip.label}
            </FilterChip>
          ))}
        </ChipGroup>
        <ChipGroup label="Severity">
          <FilterChip active={filters.severity === null} onClick={() => onFilter({ severity: null })}>Any</FilterChip>
          {SIGNAL_SEVERITIES.map((severity) => (
            <FilterChip key={severity} active={filters.severity === severity} onClick={() => onFilter({ severity })}>
              {severity}
            </FilterChip>
          ))}
        </ChipGroup>
        <ChipGroup label="Rule">
          <FilterChip active={filters.ruleId === null} onClick={() => onFilter({ ruleId: null })}>Any</FilterChip>
          {summary.rules.map((rule) => (
            <FilterChip key={rule.ruleId} active={filters.ruleId === rule.ruleId} onClick={() => onFilter({ ruleId: rule.ruleId })}>
              {ruleName(rule)}
            </FilterChip>
          ))}
        </ChipGroup>
        <ChipGroup label="Subject">
          <FilterChip active={filters.subjectKind === null} onClick={() => onFilter({ subjectKind: null })}>Any</FilterChip>
          {SIGNAL_SUBJECT_KINDS.map((kind) => (
            <FilterChip key={kind} active={filters.subjectKind === kind} onClick={() => onFilter({ subjectKind: kind })}>
              {subjectKindLabel(kind)}
            </FilterChip>
          ))}
        </ChipGroup>
        <ChipGroup label="Observation">
          <FilterChip active={filters.unconfirmedOnly} onClick={() => onFilter({ unconfirmedOnly: !filters.unconfirmedOnly })}>
            Unconfirmed only
          </FilterChip>
        </ChipGroup>
        {summary.handlingProvisioned === true ? (
          <ChipGroup label="Handling">
            <FilterChip active={filters.handling === null} onClick={() => onFilter({ handling: null })}>Any</FilterChip>
            {SIGNAL_HANDLING_FILTERS.map((handling) => (
              <FilterChip key={handling} active={filters.handling === handling} onClick={() => onFilter({ handling })}>
                {HANDLING_FILTER_LABEL[handling]}
              </FilterChip>
            ))}
          </ChipGroup>
        ) : handlingUnavailable && (
          <Text size="xs" c="dimmed" data-signal-handling-unavailable="">{handlingUnavailable}</Text>
        )}
      </Stack>

      <div data-signal-list={listView === 'ready' ? (data!.items.length === 0 ? 'empty' : 'rows') : listView}>
        {listView === 'error' ? (
          <SignalErrorState error={list.error} />
        ) : listView === 'loading' ? (
          <SignalTable signals={[]} detailHref={detailHref} loading />
        ) : data!.items.length === 0 ? (
          filters.page > 1 && data!.totalCount > 0 ? (
            <Stack align="center" gap={4} py="lg" data-signal-past-end="">
              <Text size="sm">This page is past the end of the list.</Text>
              <Anchor component="button" size="sm" onClick={() => onPage(1)}>Back to the first page</Anchor>
            </Stack>
          ) : (
            <SignalEmptyState view={emptyView(summary, hasInboxFilters(filters))} />
          )
        ) : (
          <>
            <SignalTable signals={data!.items} detailHref={detailHref} />
            {pages > 1 && (
              <Group justify="center" py="sm">
                <Pagination total={pages} value={Math.min(filters.page, pages)} onChange={onPage} size="sm" />
              </Group>
            )}
          </>
        )}
      </div>

      <Text size="xs" c="dimmed" px="md" py="sm">
        The stale-run rule signals only runs inside the resume window;{' '}
        <Anchor component={Link} to="/execution-history?status=stale" size="xs">older stale runs are in Execution History</Anchor>.
      </Text>
    </SectionCard>
  )
}
